import React from 'react';
import { Box, Container, Grid, Typography, Stack, Link, Divider, alpha, useTheme, IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const Footer: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const isProaPage = location.pathname === '/proa';

    // Navegación interna y externa específica de la página madre de PROA.
    const handleProaNavigation = (target: string) => {
        if (target.startsWith('/')) {
            navigate(target);
            return;
        }

        if (target === 'top') {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            return;
        }

        document
            .getElementById(target)
            ?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
    };

    // Función original de PROAPricing. Se mantiene intacta.
    const handleNav = (sectionId: string) => {
        if (location.pathname === '/') {
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/');
            setTimeout(() => {
                document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    if (isProaPage) {
        const proaFooterLinkSx = {
            textAlign: 'left',
            justifyContent: 'flex-start',
            width: 'fit-content',
            color: '#94a3b8',
            fontSize: '0.88rem',
            lineHeight: 1.5,
            transition: 'color 0.2s ease, transform 0.2s ease',
            '&:hover': {
                color: '#f8fafc',
                transform: 'translateX(2px)',
            },
        };

        const proaFooterHeadingSx = {
            color: '#e2e8f0',
            fontWeight: 900,
            mb: 2.5,
            textTransform: 'uppercase',
            fontSize: '0.69rem',
            letterSpacing: '1.45px',
        };

        return (
            <Box
                component="footer"
                sx={{
                    bgcolor: '#070d18',
                    color: '#94a3b8',
                    py: { xs: 7, md: 9 },
                    borderTop: `1px solid ${alpha('#94a3b8', 0.1)}`,
                }}
            >
                <Container maxWidth="lg">
                    <Grid
                        container
                        spacing={{ xs: 5, md: 6 }}
                        alignItems="flex-start"
                    >
                        {/* MARCA PROA */}
                        <Grid item xs={12} md={5}>
                            <Stack spacing={2.2}>
                                <Box
                                    onClick={() => handleProaNavigation('top')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: 'fit-content',
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s ease',
                                        '&:hover': {
                                            opacity: 0.78,
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/logo.png"
                                        alt="PROA"
                                        sx={{
                                            height: 30,
                                            mr: 1.4,
                                            filter:
                                                'drop-shadow(0 0 8px rgba(56,189,248,0.22))',
                                        }}
                                    />

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#f8fafc',
                                            fontWeight: 900,
                                            letterSpacing: '-0.5px',
                                        }}
                                    >
                                        PROA
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color: '#e2e8f0',
                                        maxWidth: 430,
                                        fontSize: {
                                            xs: '1.05rem',
                                            md: '1.12rem',
                                        },
                                        fontWeight: 800,
                                        lineHeight: 1.55,
                                    }}
                                >
                                    Datos más claros para decisiones industriales más precisas.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        maxWidth: 430,
                                        color: '#64748b',
                                        lineHeight: 1.75,
                                    }}
                                >
                                    Una marca construida para conectar información comercial y
                                    operativa con las decisiones reales de cada empresa.
                                </Typography>

                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        component="a"
                                        href="https://www.linkedin.com/company/proapricing/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="PROA en LinkedIn"
                                        size="small"
                                        sx={{
                                            color: '#64748b',
                                            border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                                            transition:
                                                'color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                                            '&:hover': {
                                                color: '#38bdf8',
                                                borderColor: alpha('#38bdf8', 0.32),
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <LinkedInIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Grid>

                        {/* NAVEGACIÓN INTERNA */}
                        <Grid item xs={6} md={2}>
                            <Typography sx={proaFooterHeadingSx}>
                                Explorar PROA
                            </Typography>

                            <Stack spacing={1.5}>
                                <Link
                                    component="button"
                                    onClick={() =>
                                        handleProaNavigation('proa-about')
                                    }
                                    underline="none"
                                    sx={proaFooterLinkSx}
                                >
                                    Visión
                                </Link>

                                <Link
                                    component="button"
                                    onClick={() =>
                                        handleProaNavigation('proa-logic')
                                    }
                                    underline="none"
                                    sx={proaFooterLinkSx}
                                >
                                    Soluciones
                                </Link>

                                <Link
                                    component="button"
                                    onClick={() =>
                                        handleProaNavigation('proa-workflow')
                                    }
                                    underline="none"
                                    sx={proaFooterLinkSx}
                                >
                                    Método
                                </Link>

                                <Link
                                    component="button"
                                    onClick={() =>
                                        handleProaNavigation('proa-contact')
                                    }
                                    underline="none"
                                    sx={proaFooterLinkSx}
                                >
                                    Contacto
                                </Link>
                            </Stack>
                        </Grid>

                        {/* SOLUCIONES */}
                        <Grid item xs={6} md={2}>
                            <Typography sx={proaFooterHeadingSx}>
                                Soluciones
                            </Typography>

                            <Stack spacing={1.5}>
                                <Link
                                    component="button"
                                    onClick={() => handleProaNavigation('/')}
                                    underline="none"
                                    sx={{
                                        ...proaFooterLinkSx,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: alpha('#38bdf8', 0.82),
                                        '&:hover': {
                                            color: '#38bdf8',
                                            transform: 'translateX(2px)',
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: '#38bdf8',
                                            boxShadow: `0 0 9px ${alpha(
                                                '#38bdf8',
                                                0.5
                                            )}`,
                                            flexShrink: 0,
                                        }}
                                    />

                                    PROAPricing
                                </Link>

                                <Link
                                    component="button"
                                    onClick={() =>
                                        handleProaNavigation('/industrial')
                                    }
                                    underline="none"
                                    sx={{
                                        ...proaFooterLinkSx,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: alpha('#22c55e', 0.82),
                                        '&:hover': {
                                            color: '#22c55e',
                                            transform: 'translateX(2px)',
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: '#22c55e',
                                            boxShadow: `0 0 9px ${alpha(
                                                '#22c55e',
                                                0.5
                                            )}`,
                                            flexShrink: 0,
                                        }}
                                    />

                                    PROAIndustrial
                                </Link>
                            </Stack>
                        </Grid>

                        {/* CONTACTO */}
                        <Grid item xs={12} md={3}>
                            <Typography sx={proaFooterHeadingSx}>
                                Contacto
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#94a3b8',
                                    fontSize: '0.88rem',
                                    lineHeight: 1.7,
                                    maxWidth: 260,
                                }}
                            >
                                ¿Todavía no sabes qué dimensión conviene abordar
                                primero?
                            </Typography>

                            <Link
                                component="button"
                                onClick={() =>
                                    handleProaNavigation('proa-contact')
                                }
                                underline="none"
                                sx={{
                                    mt: 2.2,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: '#f8fafc',
                                    fontSize: '0.88rem',
                                    fontWeight: 900,
                                    textAlign: 'left',
                                    transition:
                                        'color 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                        color: '#a78bfa',
                                        transform: 'translateX(3px)',
                                    },
                                }}
                            >
                                Hablar con PROA&nbsp;→
                            </Link>
                        </Grid>
                    </Grid>

                    <Divider
                        sx={{
                            my: { xs: 5, md: 6 },
                            borderColor: alpha('#94a3b8', 0.1),
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {
                                xs: 'column',
                                sm: 'row',
                            },
                            justifyContent: 'space-between',
                            alignItems: {
                                xs: 'flex-start',
                                sm: 'center',
                            },
                            gap: 2.5,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#64748b',
                                lineHeight: 1.7,
                            }}
                        >
                            &copy; {new Date().getFullYear()} PROA
                        </Typography>

                        <Stack
                            direction={{
                                xs: 'column',
                                sm: 'row',
                            }}
                            spacing={{
                                xs: 1.4,
                                sm: 3,
                            }}
                            alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                            }}
                        >
                            <Link
                                component="button"
                                onClick={() => navigate('/privacy')}
                                underline="none"
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.82rem',
                                    textAlign: 'left',
                                    '&:hover': {
                                        color: '#f8fafc',
                                    },
                                }}
                            >
                                Política de Privacidad
                            </Link>

                            <Link
                                component="button"
                                onClick={() => navigate('/terms')}
                                underline="none"
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.82rem',
                                    textAlign: 'left',
                                    '&:hover': {
                                        color: '#f8fafc',
                                    },
                                }}
                            >
                                Términos de Uso
                            </Link>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box 
            component="footer" 
            sx={{ 
                bgcolor: '#0B0F19',
                color: 'text.secondary',
                py: 8,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={8}>
                    {/* COLUMNA 1: MARCA */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={2}>
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.5px' }}>
                                PROAPricing
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    lineHeight: 1.8, 
                                    maxWidth: '300px',
                                    textAlign: 'justify', // <--- LA CLAVE DE LA SIMETRÍA
                                    display: 'block'
                                }}
                            >
                                Infraestructura de inteligencia de precios para empresas que exigen precisión financiera. Eliminamos la suposición de la ecuación de ingresos.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <IconButton 
                                    component="a"
                                    href="https://www.linkedin.com/company/proapricing/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small" 
                                    sx={{ 
                                        color: 'text.secondary', 
                                        transition: 'all 0.2s',
                                        '&:hover': { color: '#0077b5', transform: 'translateY(-2px)' } // Color oficial LinkedIn + Micro-interacción
                                    }}
                                >
                                    <LinkedInIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* COLUMNA 2: PRODUCTO (Navegación Interna) */}
                    <Grid item xs={6} md={2}>
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 3, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>
                            Producto
                        </Typography>
                        <Stack spacing={1.5}>
                            <Link component="button" onClick={() => handleNav('product-section')} underline="none" color="inherit" sx={{ textAlign: 'left', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Tecnología
                            </Link>
                            <Link component="button" onClick={() => handleNav('pricing-section')} underline="none" color="inherit" sx={{ textAlign: 'left', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Planes y Precios
                            </Link>
                            <Link component="button" onClick={() => handleNav('contact-form')} underline="none" color="inherit" sx={{ textAlign: 'left', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Consultoría Enterprise
                            </Link>
                        </Stack>
                    </Grid>

                    {/* COLUMNA 3: COMPAÑÍA (Navegación Mixta) */}
                    <Grid item xs={6} md={2}>
                        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 3, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>
                            Compañía
                        </Typography>
                        <Stack spacing={1.5}>
                            <Link component="button" onClick={() => handleNav('origin-section')} underline="none" color="inherit" sx={{ textAlign: 'left', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Origen
                            </Link>
                            <Link href="/login" underline="none" color="inherit" sx={{ fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Iniciar Sesión
                            </Link>
                            <Link href="/signup" underline="none" color="inherit" sx={{ fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                                Crear Cuenta
                            </Link>
                        </Stack>
                    </Grid>

                    {/* COLUMNA 4: ESTADO */}
                    <Grid item xs={12} md={4}>
                         <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 3, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>
                            Estado del Sistema
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', boxShadow: `0 0 10px ${theme.palette.success.main}` }} />
                            <Box>
                                <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 700, display: 'block' }}>
                                    Sistemas Operativos
                                </Typography>
                                <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                                    Uptime 100%
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
                

                <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.1) }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* TEXTO CORREGIDO */}
                    <Typography variant="body2" sx={{ 
                                lineHeight: 1.8,
                                textAlign: 'justify',
                                display: 'block',
                                // CAMBIO CLAVE: Usamos width fijo en escritorio, no maxWidth.
                                // Esto fuerza al texto a estirarse para llenar los 290px.
                                width: { xs: '100%', md: '290px' }, 
                            }}>
                        &copy; {new Date().getFullYear()} PROAPricing
                    </Typography>
                    
                    <Stack direction="row" spacing={3}>
                        <Link href="/privacy" underline="none" color="inherit" sx={{ fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>
                            Política de Privacidad
                        </Link>
                        <Link href="/terms" underline="none" color="inherit" sx={{ fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>
                            Términos de Uso
                        </Link>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;